import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, IconButton, Select, MenuItem,
} from '@mui/material';
import { Images } from '../../common';

function Pagination({
  currentPage, pageSize, totalPages, totalCounts, onPageChange, setPageSize, styles,
}) {
  const { leftVectorPage, rightVectorPage } = Images;
  const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
  const offset = (currentPage - 1) * pageSize + 1;
  const handlePrevClick = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const MAX_VISIBLE_PAGES = 5; // Adjust this value based on your design

  const visiblePages = pages.slice(
    Math.max(currentPage - Math.floor(MAX_VISIBLE_PAGES / 2), 0),
    Math.min(currentPage + Math.ceil(MAX_VISIBLE_PAGES / 2), totalPages),
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: 2,
        borderTop: '2px solid #ECECF0',
        margin: '24px 32px 0 0', // improved margin
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        minHeight: '64px',
        ...styles,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          sx={{
            mr: 1, borderRadius: '50%', border: '1px solid #ECECF0', background: '#F7F8FA',
          }}
          onClick={handlePrevClick}
          disabled={currentPage === 1}
          size="small"
        >
          <img src={leftVectorPage} alt="prev" />
        </IconButton>
        {visiblePages.map((page) => (
          <Button
            variant={currentPage === page ? 'contained' : 'outlined'}
            disabled={currentPage === page}
            onClick={() => onPageChange(page)}
            key={page}
            size="small"
            sx={{
              lineHeight: '17px',
              fontSize: '14px',
              fontWeight: 400,
              padding: '4px 12px',
              color: currentPage === page ? '#fff' : '#293855',
              background: currentPage === page ? '#1565c0' : '#fff',
              minWidth: '32px',
              margin: '0 2px',
              boxShadow: currentPage === page ? '0 2px 8px rgba(21,101,192,0.08)' : 'none',
              '&.Mui-disabled': {
                color: '#fff',
                backgroundColor: '#1565c0',
                fontWeight: 500,
              },
            }}
          >
            { page }
          </Button>
        ))}
        <IconButton
          sx={{
            ml: 1, borderRadius: '50%', border: '1px solid #ECECF0', background: '#F7F8FA',
          }}
          onClick={handleNextClick}
          disabled={currentPage === totalPages}
          size="small"
        >
          <img src={rightVectorPage} alt="next" />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginRight: '24px',
          fontSize: '14px',
        }}
      >
        <span style={{ fontWeight: 500 }}>Rows Per Page:</span>
        <Select
          value={pageSize}
          onChange={(e) => setPageSize(e.target.value)}
          inputProps={{ 'aria-label': 'Without label' }}
          sx={{
            minWidth: '64px',
            fontSize: '14px',
            background: '#F7F8FA',
            borderRadius: '4px',
            '& div': {
              padding: '2px 24px 2px 8px',
            },
          }}
        >
          <MenuItem value={5}>5</MenuItem>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </Select>
        <span style={{ color: '#888', margin: '0 8px' }}>|</span>
        <span>
          <b>{offset}</b>
          -
          <b>{Math.min(offset + pageSize - 1, totalCounts)}</b>
          &nbsp;of&nbsp;
          <b>{totalCounts}</b>
        </span>
      </Box>
    </Box>
  );
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalCounts: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  styles: PropTypes.shape({}),
};

export default Pagination;
